import React, { Component, Fragment } from 'react';
import cn from 'classnames';
import round from 'lodash/round';
import { rerenderOnResize } from '../../decorators';
import {
  getHumanReadableFileSize,
  fitDimensionsIntoLimits,
  stringifyTimePeriod,
  shortenFileName,
  isWebImage,
  isWebVideo,
} from '../../utils/utilsGlobal';

import { LoadingSpinner } from '../Loading';
import styles from './ChatMessagesAttachments.scss';

export const MAX_THUMBNAIL_SIZE = 325; // same as $size-attachment-preview in src/styles/variables.scss
export const MAX_STICKER_SIZE = 180;   // same as $size-sticker-preview in src/styles/variables.scss

@rerenderOnResize()
export class ChatMessagesAttachments extends Component {

  state = {
    files: [],
    images: [],
    videos: [],
  };

  MOBILE_HORIZONTAL_PADDINGS = 80;

  componentDidMount(){
    this.onAttachmentChange(this.props.attachments);
  }

  componentDidUpdate(prevProps){
    const { attachments } = this.props;
    if (attachments !== prevProps.attachments) {
      this.onAttachmentChange(attachments);
    }
  }

  onAttachmentChange = (attachments) => {
    const images = [];
    const videos = [];
    const files = [];
    const maxThumbnailSize = Math.min(MAX_THUMBNAIL_SIZE, window.innerWidth - this.MOBILE_HORIZONTAL_PADDINGS);
    const maxStickerSize = Math.min(MAX_STICKER_SIZE, window.innerWidth - this.MOBILE_HORIZONTAL_PADDINGS);

    attachments.forEach(attachment => {
      const { contentType, width, height, isSticker } = attachment;
      const thumbnailUrl = attachment.thumbnails?.[0]?.url || '';
      const maxSize = isSticker ? maxStickerSize : maxThumbnailSize;
      const [ thumbnailWidth, thumbnailHeight ] = fitDimensionsIntoLimits(width, height, maxSize, maxSize);

      if (isWebImage(contentType) && thumbnailUrl) {
        images.push({
          ...attachment,
          thumbnailWidth,
          thumbnailHeight,
          thumbnailUrl,
        });
      }
      else if (isWebVideo(contentType) && thumbnailUrl) {
        videos.push({
          ...attachment,
          thumbnailWidth,
          thumbnailHeight,
          thumbnailUrl,
        });
      }
      else {
        files.push({
          ...attachment,
          thumbnailUrl,
        });
      }
    });
    this.setState({ images, videos, files });
  };

  onPreviewClick = (e, attachmentId) => {
    const { onPreviewClick } = this.props;
    onPreviewClick(attachmentId);
    e.preventDefault();
  };

  onThumbnailError = (e) => {
    e.target.parentNode.classList.add(styles.previewsThumbnailNotFound);
  };

  render(){
    const { files, images, videos } = this.state;
    const { isSubmitting } = this.props;

    return (
      <Fragment>
        {Boolean(files.length) && (
          <ul className={styles.files}>
            {files.map(file => (
              <li key={file.id} className={styles.filesItem}>
                <a className={cn({
                  [styles.filesPreview]: true,
                  [styles.filesPreviewImage]: file.thumbnailUrl,
                  [styles.filesPreviewSubmitting]: isSubmitting,
                })}
                  style={{ backgroundImage: `url(${file.thumbnailUrl})` }}
                  href={file.url}
                  target="_blank">

                  {(!file.thumbnailUrl && !isSubmitting) &&
                    (<i className="icon-file"/>)
                  }
                  {isSubmitting && (
                    <LoadingSpinner size="xs"/>
                  )}
                </a>
                <div className={styles.filesText}>
                  <a className={styles.filesTextLink} href={file.url} target="_blank">{file.name}</a>
                  <br/>
                  <span className={styles.filesTextSecondary}>
                    {isSubmitting ? 'Загрузка...' : getHumanReadableFileSize(file.bytesSize)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}

        {Boolean(videos.length) && (
          <ul className={styles.previews}>
            {videos.map(video => (
              <li key={video.id} className={styles.previewsItem}>
                <a className={cn({
                  [styles.previewsLink]: true,
                  [styles.previewsLinkLoading]: video.status !== 'READY',
                  [styles.previewsThumbnailNotFound]: !video.thumbnailUrl,
                })}
                  href={video.url}
                  target="_blank"
                  onClick={e => this.onPreviewClick(e, video.id)}>
                  <img className={styles.previewsThumbnail}
                    src={video.thumbnailUrl}
                    alt={video.name}
                    width={round(video.thumbnailWidth)}
                    height={round(video.thumbnailHeight)}
                    onError={this.onThumbnailError}/>
                  <span className={styles.previewsThumbnailNotFoundPlaceholder}>
                    Файл не найден<br/>{shortenFileName(video.name, 25)}
                  </span>
                  <span className={styles.videoPlayIcon}>&#x25B6;</span>
                  <span className={styles.videoDuration}>
                    {stringifyTimePeriod(video.duration * 1000, { format: 'numeric' })}
                  </span>
                  <LoadingSpinner className={styles.previewSpinner} size="xs"/>
                </a>
              </li>
            ))}
          </ul>
        )}

        {Boolean(images.length) && (
          <ul className={styles.previews}>
            {images.map(image => (
              <li key={image.id} className={styles.previewsItem}>
                <a className={cn({
                  [styles.previewsLink]: true,
                  [styles.previewsLinkLoading]: isSubmitting,
                  [styles.previewsLinkSticker]: image.isSticker,
                  [styles.previewsThumbnailNotFound]: !image.thumbnailUrl,
                })}
                  href={image.url}
                  target="_blank"
                  onClick={e => this.onPreviewClick(e, image.id)}>
                  <img className={styles.previewsThumbnail}
                    src={image.thumbnailUrl}
                    alt={image.name}
                    width={round(image.thumbnailWidth)}
                    height={round(image.thumbnailHeight)}
                    onError={this.onThumbnailError}/>
                  <span className={styles.previewsThumbnailNotFoundPlaceholder}>
                    Файл не найден<br/>{shortenFileName(image.name, 25)}
                  </span>
                  <LoadingSpinner className={styles.previewSpinner} size="xs"/>
                </a>
              </li>
            ))}
          </ul>
        )}
      </Fragment>
    );
  }
}
